import React, { useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Divider,
  Input,
  Modal,
  Select,
  Skeleton,
  Space,
  Switch,
  Tag,
  Typography,
} from "antd";
import {
  ExclamationCircleOutlined,
  ReloadOutlined,
  RocketOutlined,
  SaveOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { usePlatformStatus } from "../../../hooks/usePlatformStatus";
import { getSchools } from "../../../services/schools/school.service";
import { SchoolEntity } from "../../../services/schools/school.types";
import { PLATFORM_STATUS_TAGS } from "../../../constants/status-colors";
import {
  formatLaunchDate,
  fromLaunchIso,
  toLaunchIso,
} from "../../../utils/studentLaunch";
import "./styles.scss";

const { Title, Text, Paragraph } = Typography;

export const DEFAULT_MAINTENANCE_MESSAGE =
  "La plataforma se encuentra cerrada por mantenimiento de verano. " +
  "Estará disponible nuevamente el 10 de agosto. ¡Nos vemos el próximo curso!";

// Order-insensitive comparison: the Select keeps user picking order while the
// backend may return the saved list in a different one.
const sameIdSet = (a: number[], b: number[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

const SettingsPage: React.FC = () => {
  const {
    status,
    loading,
    saving,
    loadError,
    fetchStatus,
    updateStatus,
  } = usePlatformStatus();

  // antd static Modal.confirm is not supported under React 19; use the hook API
  const [modal, modalContextHolder] = Modal.useModal();
  const [messageDraft, setMessageDraft] = useState("");

  // School list for the "allowed schools during maintenance" selector
  const [schools, setSchools] = useState<SchoolEntity[]>([]);
  const [schoolsLoading, setSchoolsLoading] = useState(true);
  const [schoolsError, setSchoolsError] = useState(false);
  const [allowedSchoolIds, setAllowedSchoolIds] = useState<number[]>([]);
  const [allowCoordinators, setAllowCoordinators] = useState(false);

  // SCRUM-768: launch countdown for the student app. Kept apart from the
  // maintenance draft above because they are two independent settings that
  // save through two different buttons.
  const [launchDraft, setLaunchDraft] = useState<Dayjs | null>(null);

  // Keep the local draft in sync with the last saved message
  useEffect(() => {
    setMessageDraft(status?.message ?? "");
  }, [status]);

  // Preselect the currently allowed schools from the saved status
  useEffect(() => {
    setAllowedSchoolIds(status?.allowed_school_ids ?? []);
  }, [status]);

  // Mirror the saved coordinators-access flag into the local draft
  useEffect(() => {
    setAllowCoordinators(status?.allow_coordinators ?? false);
  }, [status]);

  // Mirror the saved launch date into the picker, on Mexico City's clock
  useEffect(() => {
    setLaunchDraft(fromLaunchIso(status?.student_launch_at ?? null));
  }, [status]);

  // Load the schools once on mount
  useEffect(() => {
    let active = true;
    const loadSchools = async () => {
      setSchoolsLoading(true);
      setSchoolsError(false);
      try {
        const data = await getSchools();
        if (active) setSchools(data);
      } catch (error) {
        if (active) setSchoolsError(true);
      } finally {
        if (active) setSchoolsLoading(false);
      }
    };
    loadSchools();
    return () => {
      active = false;
    };
  }, []);

  const normalizedMessage = messageDraft.trim() || null;
  const messageChanged = normalizedMessage !== (status?.message ?? null);
  const schoolsChanged = !sameIdSet(
    allowedSchoolIds,
    status?.allowed_school_ids ?? []
  );
  const coordinatorsChanged =
    allowCoordinators !== (status?.allow_coordinators ?? false);
  const configChanged = messageChanged || schoolsChanged || coordinatorsChanged;

  const handleToggle = (checked: boolean) => {
    if (!checked) {
      // Deactivating is low impact: no confirmation needed
      updateStatus({
        maintenance_mode: false,
        message: normalizedMessage,
        allowed_school_ids: allowedSchoolIds,
        allow_coordinators: allowCoordinators,
      });
      return;
    }

    modal.confirm({
      title: "Activar modo mantenimiento",
      icon: <ExclamationCircleOutlined />,
      content:
        "Al activar el modo mantenimiento se cierra el acceso a toda la " +
        "plataforma, salvo las escuelas seleccionadas y los administradores. " +
        "¿Continuar?",
      okText: "Activar",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: () =>
        updateStatus({
          maintenance_mode: true,
          message: normalizedMessage,
          allowed_school_ids: allowedSchoolIds,
          allow_coordinators: allowCoordinators,
        }),
    });
  };

  const handleSaveConfig = () => {
    if (!status) return;
    updateStatus({
      maintenance_mode: status.maintenance_mode,
      message: normalizedMessage,
      allowed_school_ids: allowedSchoolIds,
      allow_coordinators: allowCoordinators,
    });
  };

  // --- SCRUM-768: student app launch countdown ------------------------------

  const launchDraftIso = toLaunchIso(launchDraft);
  const savedLaunchIso = status?.student_launch_at ?? null;
  const launchChanged = launchDraftIso !== savedLaunchIso;
  const savedLaunchLabel = formatLaunchDate(savedLaunchIso);
  const launchHasPassed = savedLaunchIso
    ? new Date(savedLaunchIso).getTime() <= Date.now()
    : false;

  /**
   * Saves ONLY the launch date.
   *
   * The maintenance flag rides along because the API requires it, and it
   * carries the SAVED value rather than any draft: the two cards own separate
   * settings, and a half-edited maintenance draft must not be persisted by
   * pressing save on this one. Everything this payload omits is preserved by
   * the backend's partial merge.
   */
  const handleSaveLaunch = () => {
    if (!status) return;
    updateStatus({
      maintenance_mode: status.maintenance_mode,
      student_launch_at: launchDraftIso,
    });
  };

  return (
    <div className="settings-page">
      {modalContextHolder}

      <div className="page-header">
        <Space direction="vertical" size="small">
          <Title level={2}>Configuración</Title>
          <Paragraph type="secondary">
            Ajustes generales de la plataforma.
          </Paragraph>
        </Space>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchStatus}
          loading={loading}
        >
          Recargar
        </Button>
      </div>

      {loadError && !status ? (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar el estado de la plataforma"
          description="Verificar la conexión e intentar nuevamente."
          action={
            <Button size="small" danger onClick={fetchStatus}>
              Reintentar
            </Button>
          }
        />
      ) : (
        <Card
          className="maintenance-card"
          title={
            <Space>
              <ToolOutlined />
              <span>Mantenimiento de la plataforma</span>
              {status && (
                <Tag
                  color={
                    (status.maintenance_mode
                      ? PLATFORM_STATUS_TAGS.maintenance
                      : PLATFORM_STATUS_TAGS.operational
                    ).color
                  }
                >
                  {
                    (status.maintenance_mode
                      ? PLATFORM_STATUS_TAGS.maintenance
                      : PLATFORM_STATUS_TAGS.operational
                    ).label
                  }
                </Tag>
              )}
            </Space>
          }
        >
          {loading && !status ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : (
            <Space direction="vertical" size="middle" className="card-body">
              <Paragraph type="secondary">
                Al activar el modo mantenimiento se cierra el acceso a toda la
                plataforma, salvo las escuelas seleccionadas y los
                administradores.
              </Paragraph>

              <Space size="middle">
                <Switch
                  checked={status?.maintenance_mode ?? false}
                  onChange={handleToggle}
                  loading={saving}
                  checkedChildren="Activado"
                  unCheckedChildren="Desactivado"
                />
                <Text strong>Modo mantenimiento</Text>
              </Space>

              <Divider className="card-divider" />

              <div>
                <Text strong>
                  Escuelas con acceso durante el mantenimiento
                </Text>
                <Paragraph type="secondary">
                  Solo estas escuelas (además de los administradores) podrán
                  usar la plataforma mientras dure el mantenimiento. Dejar
                  vacío para que únicamente los administradores tengan acceso.
                </Paragraph>
                {schoolsError ? (
                  <Alert
                    type="warning"
                    showIcon
                    message="No se pudo cargar la lista de escuelas"
                  />
                ) : (
                  <Select
                    className="allowed-school-select"
                    style={{ width: "100%" }}
                    mode="multiple"
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    placeholder="Seleccionar escuelas"
                    loading={schoolsLoading}
                    disabled={saving}
                    value={allowedSchoolIds}
                    onChange={(value) => setAllowedSchoolIds(value ?? [])}
                    options={schools
                      .filter((school) => !school.deleted_at)
                      .map((school) => ({
                        value: school.id,
                        label: school.name,
                      }))}
                  />
                )}
              </div>

              <Divider className="card-divider" />

              <div>
                <Space size="middle">
                  <Switch
                    className="allow-coordinators-switch"
                    checked={allowCoordinators}
                    onChange={(checked) => setAllowCoordinators(checked)}
                    disabled={saving}
                  />
                  <Text strong>Permitir acceso a coordinadores</Text>
                </Space>
                <Paragraph type="secondary">
                  Si está activado, las personas con rol de coordinación de
                  todas las escuelas podrán ingresar a la plataforma mientras
                  dure el mantenimiento.
                </Paragraph>
              </div>

              <Divider className="card-divider" />

              <div>
                <Text strong>Mensaje para los usuarios (opcional)</Text>
                <Paragraph type="secondary">
                  Este mensaje se mostrará a los usuarios mientras la
                  plataforma esté en mantenimiento. Dejar vacío para usar el
                  mensaje por defecto.
                </Paragraph>
                <Input.TextArea
                  rows={3}
                  maxLength={500}
                  showCount
                  value={messageDraft}
                  onChange={(e) => setMessageDraft(e.target.value)}
                  placeholder={DEFAULT_MAINTENANCE_MESSAGE}
                  disabled={saving}
                />
              </div>

              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveConfig}
                loading={saving}
                disabled={!configChanged}
              >
                Guardar configuración
              </Button>
            </Space>
          )}
        </Card>
      )}

      {!loadError && (
        <Card
          className="launch-card"
          title={
            <Space>
              <RocketOutlined />
              <span>Lanzamiento de la App Alumno</span>
              {status && (
                <Tag
                  color={
                    savedLaunchIso && !launchHasPassed ? "processing" : "green"
                  }
                >
                  {savedLaunchIso && !launchHasPassed
                    ? "Countdown activo"
                    : "App abierta"}
                </Tag>
              )}
            </Space>
          }
        >
          {loading && !status ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : (
            <Space direction="vertical" size="middle" className="card-body">
              <Paragraph type="secondary">
                Hasta esta fecha, los alumnos que entran a la App Emmi ven una
                pantalla de countdown en lugar de la elección de huevo. Al
                llegar la fecha la app se abre sola, sin publicar nada.
              </Paragraph>

              <div>
                <Text strong>Fecha y hora de lanzamiento</Text>
                <Paragraph type="secondary">
                  Se interpreta en horario de Ciudad de México (CDMX). Dejar
                  vacío para quitar el countdown y abrir la app de inmediato.
                </Paragraph>
                <DatePicker
                  className="launch-date-picker"
                  style={{ width: "100%", maxWidth: 320 }}
                  showTime={{ format: "HH:mm" }}
                  format="DD/MM/YYYY HH:mm"
                  placeholder="Sin countdown"
                  disabled={saving}
                  value={launchDraft}
                  onChange={(value) => setLaunchDraft(value)}
                />
              </div>

              <Text type="secondary">
                {savedLaunchLabel
                  ? `Guardado: ${savedLaunchLabel}${
                      launchHasPassed ? " — ya pasó, la app está abierta" : ""
                    }`
                  : "Sin countdown configurado: la app está abierta."}
              </Text>

              <Button
                className="save-launch-button"
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveLaunch}
                loading={saving}
                disabled={!launchChanged}
              >
                Guardar fecha de lanzamiento
              </Button>
            </Space>
          )}
        </Card>
      )}
    </div>
  );
};

export default SettingsPage;
